import * as CSS from 'csstype'
import {
  ActionButton,
  ActionButtonProps,
  defineStylesWithBreaks,
  modalBorderStyle,
  SVG,
  useGLTranslation,
  useResolutionBreak,
  useThemedStylesWithBreaks
} from 'notability-services-common-frontend'
import {GalleryNoteContentType, getNoteDownloadURL} from 'notability-services-common-universal'
import {useAnalytics, useIsMobileApp, useLoggedIn} from 'notability-services-next-frontend'
import React, {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react'

import {addingBasePath} from '../config'
import {config} from '../config'
import {useDownloadStatus} from '../hooks/useDownloadStatus'
import {useShowsSocialFeatures} from '../hooks/useShowsSocialFeatures'
import {assertIsError} from '../util/error'
import {IOSMessageHandlers, usePreviewNote, useSendNativeMessage} from '../util/iosNative'
import {LikedNotesContext, useCurrentUserLikesNote} from '../util/LikedNotesContext'
import {CounterButton} from './CounterButton'

const templateQueryParam = '?template=true'

type DrillDownActionsProps = {
  noteId: string
  noteName: string
  hasNtb: boolean
  hasNote: boolean
  overlayShowing: boolean
  setOverlayShowing: (showing: boolean) => void
  setSpilloverPopover: (showing: boolean) => void
  numLikes: number
  numComments: number
  commentsEnabled: boolean
  onChangeLike: (status: 'like' | 'unlike') => void
  showLoginModal: () => void
  showCommentModal: () => void

  additionalButtons?: ActionButtonProps[]
  contentType: GalleryNoteContentType
}

const styles = defineStylesWithBreaks((theme, font) => {
  return {
    container: {
      display: 'flex',
      justifyContent: 'flex-start',
      gap: '16px'
    },

    popoverButtonContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    },

    popover: {
      position: 'absolute',
      top: '105%',
      ...modalBorderStyle(theme),
      background: theme.core.b1,
      zIndex: 1000
    },

    downloadPopover: {
      padding: '16px',
      width: 'max-content',
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      left: '0px',

      mobile: {
        padding: '18px',
        left: 'auto',
        right: '0px'
      }
    },

    downloadButton: {
      ...font.bodyBold
    },

    hint: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '0 20px',
      height: '40px',
      left: 0,
      transition: 'opacity 0.3s',
      whiteSpace: 'nowrap'
    },

    downloadRowContainer: {
      display: 'flex',
      justifyContent: 'stretch',
      gap: '12px',
      color: 'black',
      minWidth: '50%',
      alignItems: 'center'
    },

    downloadRowText: {
      margin: 0,
      fontSize: '16px',
      fontStyle: 'normal',
      fontWeight: 700,
      letterSpacing: '0em',
      textAlign: 'left',
      color: theme.core.f1
    },

    formatIcon: {
      width: '17px',
      height: '17px',
      backgroundColor: theme.accent.base,
      maskImage: `url(${addingBasePath('/nb-logo-submark-mono.svg')})`,
      WebkitMaskImage: `url(${addingBasePath('/nb-logo-submark-mono.svg')})`,
      maskSize: 'contain',
      WebkitMaskSize: 'contain',
      maskRepeat: 'no-repeat',
      WebkitMaskRepeat: 'no-repeat'
    },

    pdfIcon: {
      color: theme.core.f1
    }
  }
})

interface DownloadRowArgs {
  fileType: 'pdf' | 'note'
  noteId: string
  noteName: string
  contentType: GalleryNoteContentType
  dismiss: () => void
}
function DownloadRow({dismiss, fileType, noteId, noteName, contentType}: DownloadRowArgs) {
  const s = useThemedStylesWithBreaks(styles)
  const {t} = useGLTranslation()
  const {logEvent} = useAnalytics()

  const title = fileType === 'pdf' ? t('downloadPDF') : t('downloadNote')
  const href = getNoteDownloadURL(config.API_SERVICE_URL, {fileType, noteId, noteName, noteType: 'gallery'})

  return (
    <a
      style={s.downloadRowContainer}
      href={href}
      download={true}
      onClick={(e) => {
        void logEvent({
          name: 'Save Gallery Note',
          'File Format': fileType,
          'Note Type': contentType
        })
        e.stopPropagation()
        dismiss()
      }}
    >
      {fileType === 'pdf' ? <SVG.PDF {...s.pdfIcon} /> : <span role="img" aria-label={title} style={s.formatIcon} />}
      <p style={s.downloadRowText}>{title}</p>
    </a>
  )
}

type HintLocation = 'like' | 'share'

export default function DrillDownActions({
  noteId,
  noteName,
  hasNtb,
  hasNote,
  overlayShowing,
  setOverlayShowing,
  setSpilloverPopover,
  numLikes,
  numComments,
  onChangeLike,
  additionalButtons = [],
  contentType,
  showLoginModal,
  showCommentModal
}: DrillDownActionsProps) {
  const isInApp = useIsMobileApp()
  const showsSocialFeatures = useShowsSocialFeatures()
  const resBreak = useResolutionBreak()
  const {t} = useGLTranslation()

  const s = useThemedStylesWithBreaks(styles)

  const {likeNote, unlikeNote} = useContext(LikedNotesContext)
  const userLikesNote = useCurrentUserLikesNote(noteId)

  const downloadStatus = useDownloadStatus(noteId)
  const {loggedIn} = useLoggedIn()
  const {logEvent} = useAnalytics()

  const openWhenDownloaded = useRef(false)

  const isTemplate = contentType === GalleryNoteContentType.TEMPLATE

  useEffect(() => {
    if (openWhenDownloaded.current && typeof downloadStatus !== 'string') {
      openWhenDownloaded.current = false
      window.location.assign(`notability-openInApp://${downloadStatus.uuid}${isTemplate ? templateQueryParam : ''}`)
    }
  }, [isTemplate, downloadStatus])

  const previewNote = usePreviewNote({id: noteId, name: noteName, downloadStatus, contentType, hasNtb})

  const downloadButton = useMemo(() => {
    const getDownloadImage = (color: CSS.Property.Color) => {
      if (downloadStatus === 'downloading') {
        return (
          <div className={'spin'}>
            <SVG.LoadingSmall color={color} />
          </div>
        )
      } else if (resBreak === 'mobile') {
        return (
          <>
            {downloadStatus === 'undownloaded' ? (
              isInApp ? (
                <SVG.ExpandSmall color={color} />
              ) : (
                <SVG.DownArrowSmall color={color} />
              )
            ) : (
              <SVG.CheckSmall color={color} />
            )}
          </>
        )
      } else {
        return undefined
      }
    }

    let buttonTitle: string | undefined
    if (resBreak !== 'mobile') {
      if (downloadStatus === 'downloading') {
        buttonTitle = ''
      } else if (downloadStatus === 'undownloaded') {
        buttonTitle = isInApp ? t('Open') : t('Download')
      } else {
        buttonTitle = isInApp ? t('Open') : t('Open in App')
      }
    }

    const templateQueryParam = isInApp && isTemplate ? '?template=true' : ''

    return (
      <ActionButton
        style={s.downloadButton}
        size="small"
        buttonShape="rounded"
        appearance="filled"
        onClick={(e) => {
          e.stopPropagation()

          if (isInApp) {
            void logEvent({
              name: 'Save Gallery Note',
              'Note Type': contentType,
              'File Format': 'note'
            })
          } else {
            setSpilloverPopover(false)
          }

          openWhenDownloaded.current = true
          if (previewNote()) {
            return
          }

          if (downloadStatus === 'undownloaded') {
            if (isInApp) {
              window.location.assign(
                getNoteDownloadURL(config.API_SERVICE_URL, {fileType: 'note', noteId, noteName, noteType: 'gallery'}) +
                  templateQueryParam
              )
            } else {
              setOverlayShowing(!overlayShowing)
            }
          } else if (typeof downloadStatus !== 'string') {
            window.location.assign(`notability-openInApp://${downloadStatus.uuid}${templateQueryParam}`)
          }
        }}
        iconCreator={getDownloadImage}
        text={buttonTitle}
      />
    )
  }, [
    isTemplate,
    downloadStatus,
    isInApp,
    noteId,
    noteName,
    previewNote,
    overlayShowing,
    resBreak,
    s.downloadButton,
    setOverlayShowing,
    setSpilloverPopover,
    t,
    logEvent,
    contentType
  ])

  const downloadPopover = (
    <div style={{...s.popover, ...s.downloadPopover, ...(!overlayShowing && {display: 'none'})}}>
      <DownloadRow
        contentType={contentType}
        fileType="pdf"
        noteId={noteId}
        noteName={noteName}
        dismiss={() => setOverlayShowing(false)}
      />
      {/* Android publishes have no legacy .note artifact; the row would 404. Until ntb download
          handling lands here, those notes are PDF-only on the web. */}
      {hasNote && (
        <DownloadRow
          contentType={contentType}
          fileType="note"
          noteId={noteId}
          noteName={noteName}
          dismiss={() => setOverlayShowing(false)}
        />
      )}
    </div>
  )

  const [hintShown, setHintShown] = useState(false)
  const [hintLocation, setHintLocation] = useState('like' as HintLocation)
  const [hintMessage, setHintMessage] = useState('')
  const [likePopoverTimeout, setLikePopoverTimeout] = useState(null as NodeJS.Timeout | null)
  const presentNativeShareSheet = useSendNativeMessage(IOSMessageHandlers.shareLinkHandler)

  const showHintPopover = useCallback(
    (message: string, location: HintLocation) => {
      setHintMessage(message)
      setHintShown(true)
      setHintLocation(location)
      setOverlayShowing(false)
      setSpilloverPopover(false)
      if (likePopoverTimeout) clearTimeout(likePopoverTimeout)
      setLikePopoverTimeout(
        setTimeout(() => {
          setHintShown(false)
          setLikePopoverTimeout(null)
        }, 3200)
      )
    },
    [likePopoverTimeout, setOverlayShowing, setSpilloverPopover]
  )

  const onShareClick = useCallback(() => {
    const sharedURL = new URL(window.location.href)
    const {searchParams} = sharedURL
    if (!searchParams.get('referrer')) {
      searchParams.append('referrer', 'share')
    }
    const sharedHref = sharedURL.href
    if (isInApp) {
      presentNativeShareSheet({noteUrl: sharedHref})
    } else {
      void navigator.clipboard.writeText(sharedHref)
      showHintPopover(t('copyLinkHint'), 'share')
    }
  }, [isInApp, presentNativeShareSheet, showHintPopover, t])

  useEffect(() => {
    // Clear timeout on component unmount
    return () => {
      if (likePopoverTimeout) clearTimeout(likePopoverTimeout)
      setLikePopoverTimeout(null)
    }
  }, [likePopoverTimeout])

  const toggleLike = useCallback(() => {
    if (loggedIn) {
      try {
        if (userLikesNote) {
          unlikeNote(noteId)
          onChangeLike('unlike')
          showHintPopover(t('likeHintRemoved'), 'like')
        } else {
          likeNote(noteId)
          onChangeLike('like')
          showHintPopover(t('likeHintAdded'), 'like')
        }
      } catch (e) {
        assertIsError(e)
        showHintPopover(e.message, 'like')
      }
    } else {
      showLoginModal()
    }
  }, [likeNote, loggedIn, noteId, onChangeLike, showHintPopover, showLoginModal, t, unlikeNote, userLikesNote])

  const showCommentOrLoginModal = useCallback(() => {
    if (loggedIn) {
      showCommentModal()
    } else {
      showLoginModal()
    }
  }, [showCommentModal, loggedIn, showLoginModal])

  const hintPopover = <div style={{...s.popover, ...s.hint, opacity: hintShown ? 1 : 0}}>{hintMessage}</div>

  return (
    <div style={s.container}>
      <div style={s.popoverButtonContainer}>
        <ActionButton
          size="small"
          color="neutral"
          buttonShape="rounded"
          appearance="stroked"
          iconCreator={(color) => (
            <div>
              <SVG.ShareSmall color={color} />
            </div>
          )}
          onClick={onShareClick}
        />
        {hintLocation === 'share' && hintPopover}
      </div>
      {showsSocialFeatures && (
        <div style={s.popoverButtonContainer}>
          <CounterButton
            count={numLikes}
            selected={userLikesNote}
            svgName={'HeartOutlineSmall'}
            svgNameSelected={'HeartFillSmall'}
            onClick={toggleLike}
          />
          {hintLocation === 'like' && hintPopover}
        </div>
      )}
      {additionalButtons.map((props) => (
        <div key={props.key} style={s.popoverButtonContainer}>
          <ActionButton {...props} />
        </div>
      ))}

      {showsSocialFeatures && (
        <div style={s.popoverButtonContainer}>
          <CounterButton onClick={showCommentOrLoginModal} svgName={'CommentSmall'} count={numComments} />
        </div>
      )}

      <div style={s.popoverButtonContainer}>
        {downloadButton}
        {downloadPopover}
      </div>
    </div>
  )
}
